import React, { useEffect, useRef, useState } from 'react';
import { Typography, Paper } from '@mui/material';
import { Timer as TimerIcon } from '@mui/icons-material';

interface CountdownTimerProps {
  totalSeconds: number;
  onExpire: () => void;
  isPaused?: boolean;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  totalSeconds,
  onExpire,
  isPaused = false,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(totalSeconds);

  // Kept in a ref so a caller passing a non-memoized callback (the common case)
  // does not tear down the interval on every render. `onExpire` used to sit in
  // the effect's dependency array, so each re-render restarted the 1000ms window
  // and the displayed clock drifted behind wall-clock time — while the server
  // still scored against real elapsed time.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Track an absolute deadline rather than decrementing a counter, so the
  // countdown stays accurate even when ticks are delayed or the tab is throttled.
  const deadlineRef = useRef<number>(Date.now() + totalSeconds * 1000);
  const pausedAtRef = useRef<number | null>(null);

  useEffect(() => {
    deadlineRef.current = Date.now() + totalSeconds * 1000;
    pausedAtRef.current = null;
    setTimeRemaining(totalSeconds);
  }, [totalSeconds]);

  useEffect(() => {
    if (isPaused) {
      pausedAtRef.current = Date.now();
      return;
    }

    // Resuming: push the deadline out by however long we were paused.
    if (pausedAtRef.current !== null) {
      deadlineRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }

    const tick = () => {
      const secondsLeft = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setTimeRemaining(secondsLeft);

      if (secondsLeft === 0) {
        clearInterval(interval);
        onExpireRef.current();
      }
    };

    const interval = setInterval(tick, 250);
    tick();

    return () => clearInterval(interval);
  }, [isPaused]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getColor = (): string => {
    const percentRemaining = timeRemaining / totalSeconds;
    if (percentRemaining > 0.6) return 'success.main';
    if (percentRemaining > 0.3) return 'warning.main';
    return 'error.main';
  };

  const getPulse = (): boolean => {
    const percentRemaining = timeRemaining / totalSeconds;
    return percentRemaining <= 0.3;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        bgcolor: 'background.paper',
        borderRadius: 2,
        animation: getPulse() ? 'pulse 1s ease-in-out infinite' : 'none',
        '@keyframes pulse': {
          '0%, 100%': {
            transform: 'scale(1)',
          },
          '50%': {
            transform: 'scale(1.05)',
          },
        },
      }}
    >
      <TimerIcon sx={{ fontSize: 32, color: getColor() }} />
      <Typography
        variant="h4"
        component="div"
        fontWeight="bold"
        sx={{
          color: getColor(),
          fontFamily: 'monospace',
          minWidth: '100px',
          textAlign: 'center',
        }}
      >
        {formatTime(timeRemaining)}
      </Typography>
    </Paper>
  );
};

export default CountdownTimer;
